import { render } from "preact";
import { html } from "htm/preact";
import { signal } from "@preact/signals";
import { Input, BlobSource, ALL_FORMATS } from "mediabunny";
import { setupServiceWorkerReloadWhenIdle } from "./sw-reload.js";

const source = signal("");
const filename = signal("");
const duration = signal(0);
const currentTime = signal(0);
const playing = signal(false);
const priming = signal(false);
const seeking = signal(false);
const scrubbing = signal(false);
const timelineHeld = signal(false);
const message = signal("");
const fpsMessage = signal("");
const encodedFps = signal("30");
const recordedFps = signal("");
const slowMotion = signal(false);
const takeoff = signal(/** @type {number | null} */ (null));
const landing = signal(/** @type {number | null} */ (null));
const settingsOpen = signal(false);
let session = 0;
let fpsEdited = false;
let animation = 0;
let playRequest = 0;
let playbackFrame = 0;
let lastPresentedNow = 0;
let waitingForVideo = false;
let playInvoked = false;
/** @type {number | null} */
let lastPresentedTime = null;
/** @type {(() => void) | null} */
let cancelFrameWait = null;
let syncedScrollLeft = 0;
let scrubEndTimer = 0;
let scrubFrame = 0;
/** @type {number | null} */
let pendingScrubTime = null;
let mousePointer = -1;
let mouseStartX = 0;
let mouseStartOffset = 0;
/** @type {Input | null} */
let metadataInput = null;

/** @returns {HTMLVideoElement | null} */
function getVideo() {
  return /** @type {HTMLVideoElement | null} */ (document.getElementById("video"));
}

function timing() {
  const encoded = Number(encodedFps.value);
  const recorded = Number(recordedFps.value);
  const validEncoded = Number.isFinite(encoded) && encoded > 0 && encoded <= 1000;
  const validRecorded = Number.isFinite(recorded) && recorded > 0 && recorded <= 1000;
  const valid = validEncoded && (!slowMotion.value || (validRecorded && recorded >= encoded));
  let factor = 1;
  if (slowMotion.value && valid) factor = recorded / encoded;
  return { encoded, valid, factor };
}

function App() {
  const ready = duration.value > 0;
  const clock = timing();
  const start = takeoff.value;
  const end = landing.value;
  const complete = start !== null && end !== null && end > start && clock.valid;
  let flight = 0;
  if (start !== null && end !== null && complete) flight = (end - start) / clock.factor;
  const height = 9.81 * flight * flight / 8 * 100;

  return html`
    <main class="app-shell">
      <header class="header">
        <div class="brand"><img src="./icon_192.png" alt="" width="32" height="32" /><h1>Jump Lab <span>CMJ</span></h1></div>
        ${source.value && !settingsOpen.value && html`<div class="file-actions"><button onClick=${openFile}>Change video</button><button onClick=${clearVideo} aria-label="Close video">×</button></div>`}
      </header>
      <div hidden=${settingsOpen.value}>
      <section class=${source.value ? "viewer" : "viewer empty"} aria-label="Video preview">
        <video id="video" src=${source.value || undefined} playsInline muted preload="auto"
          onLoadedMetadata=${loaded} onTimeUpdate=${updateTime} onSeeked=${seeked}
          onSeeking=${function () { seeking.value = true; }}
          onPlaying=${mediaPlaying} onWaiting=${mediaWaiting}
          onPause=${paused} onEnded=${ended} onError=${mediaError}></video>
        ${!source.value && html`<div class="empty-content"><p class="eyebrow">COUNTERMOVEMENT JUMP</p><h2>One jump. Two marks.</h2><p>Load your video, mark takeoff and landing,<br />and get your jump height.</p><button class="primary" onClick=${openFile}>Load video</button><p class="privacy">Your video stays on this device.</p></div>`}
        ${source.value && html`<span class="video-name">${filename.value}</span>`}
      </section>
      <p class="status" role="status">${message.value}</p>
      ${source.value && html`
        <section class="editor" aria-label="Annotate video">
          <div class="transport">
            <div class="playback-buttons"><button disabled=${!ready || scrubbing.value || timelineHeld.value} onClick=${function () { step(-1); }} aria-label="Previous frame">−1 frame</button><button class="play" disabled=${!ready || scrubbing.value || timelineHeld.value} onClick=${togglePlayback}>${playing.value ? "Pause" : "Play"}</button><button disabled=${!ready || scrubbing.value || timelineHeld.value} onClick=${function () { step(1); }} aria-label="Next frame">+1 frame</button></div>
            <output class="time">${formatTime(currentTime.value)} <span>/ ${formatTime(duration.value)}</span></output>
          </div>
          <div class="timeline-heading"><span>Scroll to scrub · mark at the center</span><button id="timing-button" class="settings-link" onClick=${openSettings}>Video timing</button></div>
          <div id="timeline" class="timeline" onScroll=${scrollTimeline}
            onTouchStart=${holdTimeline} onTouchEnd=${releaseTouch} onTouchCancel=${releaseTouch}
            onPointerDown=${startMouseDrag} onPointerMove=${moveMouseDrag}
            onPointerUp=${endMouseDrag} onPointerCancel=${endMouseDrag} onLostPointerCapture=${endMouseDrag}
            onWheel=${wheelTimeline}>
            <div class="timeline-track" style=${{ width: `calc(100% + ${timelineWidth()}px)` }}>
              <canvas id="ruler" height="82" role="slider" tabIndex="0" aria-label="Video position" aria-valuemin="0" aria-valuemax=${duration.value} aria-valuenow=${currentTime.value} aria-valuetext=${formatTime(currentTime.value)} onKeyDown=${timelineKey}></canvas>
            </div>
          </div>
          <div class="markers">
            <${Marker} label="Takeoff" value=${start} onMark=${function () { mark("takeoff"); }} onClear=${function () { takeoff.value = null; drawTimeline(); }} disabled=${!ready || seeking.value || priming.value || scrubbing.value || timelineHeld.value} />
            <${Marker} label="Landing" value=${end} onMark=${function () { mark("landing"); }} onClear=${function () { landing.value = null; drawTimeline(); }} disabled=${!ready || seeking.value || priming.value || scrubbing.value || timelineHeld.value} />
          </div>
          <p class="hint">Takeoff: first frame with both feet off the ground. Landing: first frame touching down.</p>

        </section>
        <section class="result" aria-label="CMJ results" aria-live="polite">
          <div><p class="eyebrow">JUMP HEIGHT</p><p class="height">${complete ? height.toFixed(1) : "—"}<span> cm</span></p></div>
          <div class="result-detail">${complete ? html`<strong>${flight.toFixed(3)} s flight time</strong><span>${clock.factor.toFixed(2)}× time correction</span>` : html`<strong>Mark takeoff and landing</strong><span>Your result appears here.</span>`}</div>
          <p class="hint">Flight-time estimate · h = g × t² / 8. Assumes the same body position at takeoff and landing.</p>
        </section>`}
      </div>
      ${settingsOpen.value && html`          <section class="timing" aria-labelledby="timing-title">
            <button id="back-to-video" onClick=${closeSettings}>← Back to video</button>
            <h2 id="timing-title">Video timing & slow motion</h2>
            <p>${fpsMessage.value}</p>
            <label class="field">Encoded / rendered FPS<input type="number" min="1" max="1000" step="any" value=${encodedFps.value} onInput=${changeEncodedFps} /></label>
            <label class="check"><input type="checkbox" checked=${slowMotion.value} onChange=${function (/** @type {Event & {currentTarget: HTMLInputElement}} */ event) { slowMotion.value = event.currentTarget.checked; }} />This file was exported in slow motion</label>
            ${slowMotion.value && html`<label class="field">Original recorded FPS<input type="number" min="1" max="1000" step="any" placeholder="e.g. 240" value=${recordedFps.value} onInput=${function (/** @type {Event & {currentTarget: HTMLInputElement}} */ event) { recordedFps.value = event.currentTarget.value; }} /></label>`}
            <p>Use the file’s FPS, not your screen’s refresh rate. A 240 FPS recording exported at 30 FPS needs 8× time correction. A normal-speed 240 FPS file needs none.</p>
            <p>Recorded FPS cannot be reliably inferred. For speed ramps or duplicated frames, use a constant-speed original or export.</p>
            ${!clock.valid && html`<p class="error">Enter FPS between 1 and 1000. Recorded FPS must be at least the encoded FPS.</p>`}
          </section>`}
      <input id="file" class="file-input" type="file" accept="video/*" aria-label="Load video" onChange=${loadFile} />
    </main>`;
}

/** @param {{label: string, value: number | null, onMark: () => void, onClear: () => void, disabled: boolean}} props */
function Marker(props) {
  return html`<div class="marker"><button class="mark-button" disabled=${props.disabled} onClick=${props.onMark}><span>${props.value === null ? "Mark" : "Update"} ${props.label.toLowerCase()}</span><strong>${props.value === null ? "—" : formatTime(props.value)}</strong></button>${props.value !== null && html`<button class="remove-mark" aria-label=${`Clear ${props.label.toLowerCase()}`} onClick=${props.onClear}>×</button>`}</div>`;
}

function openFile() {
  document.getElementById("file")?.click();
}

function clearVideo() {
  resetScrubbing();
  session++;
  metadataInput?.dispose();
  metadataInput = null;
  pausePlayback();
  if (source.value) URL.revokeObjectURL(source.value);
  source.value = "";
  filename.value = "";
  duration.value = 0;
  currentTime.value = 0;
  takeoff.value = null;
  landing.value = null;
  seeking.value = false;
  message.value = "";
  slowMotion.value = false;
  recordedFps.value = "";
  encodedFps.value = "30";
  fpsEdited = false;
  settingsOpen.value = false;
  syncedScrollLeft = 0;
}

/** @param {Event} event */
async function loadFile(event) {
  const picker = /** @type {HTMLInputElement} */ (event.currentTarget);
  const file = picker.files?.[0];
  picker.value = "";
  if (!file) return;
  clearVideo();
  const loadSession = session;
  filename.value = file.name;
  source.value = URL.createObjectURL(file);
  message.value = "Loading video…";
  fpsMessage.value = "Reading frame timestamps…";
  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  metadataInput = input;
  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track) throw new Error("No video track");
    const metrics = await track.computeFrameRateMetrics();
    if (session !== loadSession) return;
    const fps = metrics.bestGuessFrameRate;
    if (!Number.isFinite(fps) || fps <= 0 || fps > 1000) throw new Error("Unknown FPS");
    if (!fpsEdited) encodedFps.value = String(Math.round(fps * 1000) / 1000);
    fpsMessage.value = `Estimated ${fps.toFixed(3)} FPS from frame timestamps. You can override it below. Frame steps are approximate for variable-rate video.`;
  } catch {
    if (session !== loadSession) return;
    fpsMessage.value = "FPS detection unavailable. Using 30 FPS for frame steps; enter the file’s FPS below.";
  } finally {
    input.dispose();
    if (metadataInput === input) metadataInput = null;
  }
}

function loaded() {
  const video = getVideo();
  if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
    message.value = "This video has no usable duration. Try another file.";
    return;
  }
  duration.value = video.duration;
  message.value = "";
  requestAnimationFrame(syncTimeline);
}

function mediaError() {
  if (!source.value) return;
  pausePlayback();
  duration.value = 0;
  message.value = "This video cannot be played here. Try an MP4 / H.264 export or another browser.";
}

function updateTime() {
  const video = getVideo();
  // Safari timeupdate/seeked can expose an optimistic clock after a seek.
  // Only the playback painter may advance the timeline during playback.
  if (!video || playing.value || video.seeking || seeking.value || scrubbing.value || timelineHeld.value || pendingScrubTime !== null) return;
  if (Math.abs(video.currentTime - currentTime.value) > 0.045) return;
  syncTimeline();
}

function seeked() {
  const video = getVideo();
  if (!video || video.seeking) return;
  seeking.value = false;
  if (pendingScrubTime !== null) {
    flushScrubSeek();
    return;
  }
  updateTime();
}

function stopPlaybackClock() {
  cancelAnimationFrame(animation);
  animation = 0;
  const video = getVideo();
  if (video && playbackFrame) video.cancelVideoFrameCallback(playbackFrame);
  playbackFrame = 0;
  cancelFrameWait?.();
  cancelFrameWait = null;
}

function pausePlayback() {
  playRequest++;
  stopPlaybackClock();
  // Freeze on the visible frame, not Safari's possibly-ahead currentTime.
  if (playing.value && !priming.value && lastPresentedTime !== null) {
    currentTime.value = lastPresentedTime;
  }
  playing.value = false;
  priming.value = false;
  waitingForVideo = false;
  playInvoked = false;
  getVideo()?.pause();
}

function paused() {
  const video = getVideo();
  // Ignore a queued pause from an older seek when a new play has started.
  if (!video || !video.paused || !playing.value) return;
  if (priming.value && !playInvoked) return;
  pausePlayback();
  syncTimeline();
}

function ended() {
  const video = getVideo();
  if (!video?.ended) return;
  pausePlayback();
  currentTime.value = duration.value;
  syncTimeline();
}

function mediaWaiting() {
  if (!playing.value) return;
  waitingForVideo = true;
}

function mediaPlaying() {
  // With frame callbacks, only a presented frame clears a decoder stall.
  const video = getVideo();
  if (video && typeof video.requestVideoFrameCallback !== "function") waitingForVideo = false;
}

/** @param {number} request */
function watchPresentedFrames(request) {
  const video = getVideo();
  if (!video || request !== playRequest || !playing.value) return;
  playbackFrame = video.requestVideoFrameCallback(function (now, metadata) {
    playbackFrame = 0;
    if (request !== playRequest || !playing.value) return;
    if (!video.seeking && metadata.mediaTime >= (lastPresentedTime ?? 0)) {
      lastPresentedTime = metadata.mediaTime;
      lastPresentedNow = now;
      waitingForVideo = false;
    }
    watchPresentedFrames(request);
  });
}

function tick() {
  const video = getVideo();
  if (!video || !playing.value || priming.value || video.paused) return;
  let time = video.currentTime;
  if (waitingForVideo) time = currentTime.value;
  else if (lastPresentedTime !== null) {
    // Sportschrono's bounded presented-frame clock: smooth between frames,
    // but never let a stalled decoder leave the timeline running ahead.
    const elapsed = Math.max(0, (performance.now() - lastPresentedNow) / 1000);
    const projection = Math.min(elapsed, 0.12) * video.playbackRate;
    const presentedTime = lastPresentedTime + projection;
    time = Math.min(time, presentedTime);
  }
  currentTime.value = Math.min(duration.value, Math.max(currentTime.value, time));
  syncTimeline();
  animation = requestAnimationFrame(tick);
}

/**
 * @param {HTMLVideoElement} video
 * @param {number} target
 * @param {number} request
 * @returns {Promise<boolean>}
 */
async function waitForSeek(video, target, request) {
  const deadline = performance.now() + 700;
  while (request === playRequest) {
    const atTarget = Math.abs(video.currentTime - target) <= 0.045;
    if (!video.seeking && video.readyState >= 2 && atTarget) return true;
    if (performance.now() >= deadline) return false;
    await new Promise(function (resolve) { window.setTimeout(resolve, 20); });
  }
  return false;
}

/**
 * @param {HTMLVideoElement} video
 * @param {number} timeout
 * @returns {Promise<{mediaTime: number, now: number} | null>}
 */
function nextPresentedFrame(video, timeout) {
  return new Promise(function (resolve) {
    let handle = 0;
    const timer = window.setTimeout(function () { finish(null); }, timeout);
    /** @param {{mediaTime: number, now: number} | null} frame */
    function finish(frame) {
      window.clearTimeout(timer);
      video.cancelVideoFrameCallback(handle);
      cancelFrameWait = null;
      resolve(frame);
    }
    cancelFrameWait = function () { finish(null); };
    handle = video.requestVideoFrameCallback(function (now, metadata) {
      finish({ mediaTime: metadata.mediaTime, now });
    });
  });
}

async function togglePlayback() {
  const video = getVideo();
  if (!video || scrubbing.value || timelineHeld.value) return;
  if (playing.value) {
    pausePlayback();
    syncTimeline();
    return;
  }
  pausePlayback();
  const request = playRequest;
  const target = video.ended || currentTime.value >= duration.value ? 0 : currentTime.value;
  const supportsFrames = typeof video.requestVideoFrameCallback === "function";
  currentTime.value = target;
  playing.value = true;
  priming.value = true;
  lastPresentedTime = null;
  waitingForVideo = false;
  message.value = "";
  syncTimeline();
  try {
    // Hold the displayed target while Safari settles its seek. Retry once;
    // delayed seeked events must not start playback from an earlier position.
    if (Math.abs(video.currentTime - target) > 0.00001) video.currentTime = target;
    let ready = await waitForSeek(video, target, request);
    if (request !== playRequest) return;
    if (!ready) {
      video.currentTime = target;
      ready = await waitForSeek(video, target, request);
    }
    if (request !== playRequest) return;
    if (!ready) throw new Error("Video seek did not settle");
    seeking.value = false;
    if (supportsFrames) await nextPresentedFrame(video, 180);
    if (request !== playRequest) return;
    playInvoked = true;
    await video.play();
    if (request !== playRequest) return;
    if (supportsFrames) {
      const deadline = performance.now() + 1400;
      let advanced = false;
      while (request === playRequest && performance.now() < deadline) {
        const frame = await nextPresentedFrame(video, 80);
        if (request !== playRequest) return;
        // A callback for the paused seek frame is not evidence of playback.
        if (!frame || frame.mediaTime < target + 0.004) continue;
        lastPresentedTime = frame.mediaTime;
        lastPresentedNow = frame.now;
        currentTime.value = Math.max(target, frame.mediaTime);
        advanced = true;
        break;
      }
      if (request !== playRequest) return;
      if (!advanced) throw new Error("Video did not present an advancing frame");
      watchPresentedFrames(request);
    }
    priming.value = false;
    tick();
  } catch {
    if (request !== playRequest) return;
    pausePlayback();
    message.value = "Playback could not start at this frame. Try Play again or move to another frame.";
  }
}

/** @param {number} time */
function seek(time) {
  const video = getVideo();
  if (!video || !duration.value) return;
  pausePlayback();
  const target = Math.min(Math.max(time, 0), duration.value);
  seeking.value = target !== video.currentTime;
  video.currentTime = target;
  currentTime.value = target;
  syncTimeline();
}

/** @param {number} direction */
function step(direction) {
  const clock = timing();
  if (!clock.valid) { message.value = "Check the video timing settings before stepping frames."; return; }
  seek(currentTime.value + direction / clock.encoded);
}

/** @param {"takeoff" | "landing"} key */
function mark(key) {
  const video = getVideo();
  if (!video || video.seeking || seeking.value || priming.value || scrubbing.value || timelineHeld.value || pendingScrubTime !== null) return;
  pausePlayback();
  syncTimeline();
  const time = currentTime.value;
  if (key === "landing" && takeoff.value !== null && time <= takeoff.value) {
    message.value = "Landing must be after takeoff. Move to a later frame.";
    return;
  }
  if (key === "takeoff" && landing.value !== null && time >= landing.value) {
    message.value = "Takeoff must be before landing. Move to an earlier frame or clear landing.";
    return;
  }
  if (key === "takeoff") takeoff.value = time;
  else landing.value = time;
  message.value = "";
  drawTimeline();
}

/** @param {Event} event */
function changeEncodedFps(event) {
  fpsEdited = true;
  encodedFps.value = /** @type {HTMLInputElement} */ (event.currentTarget).value;
}

function openSettings() {
  pausePlayback();
  settingsOpen.value = true;
  requestAnimationFrame(function () {
    document.getElementById("back-to-video")?.focus();
  });
}

function closeSettings() {
  settingsOpen.value = false;
  requestAnimationFrame(function () {
    syncTimeline();
    document.getElementById("timing-button")?.focus();
  });
}

function timelineWidth() {
  return Math.max(800, duration.value * 100);
}

// Programmatic scrolling follows playback and frame steps. Only a different
// scroll position is user input; otherwise scroll events would pause playback.
function syncTimeline() {
  const viewport = document.getElementById("timeline");
  if (!viewport || !viewport.clientWidth || !duration.value || scrubbing.value || timelineHeld.value) return;
  const target = currentTime.value / duration.value * timelineWidth();
  if (Math.abs(viewport.scrollLeft - target) > 0.5) viewport.scrollLeft = target;
  syncedScrollLeft = viewport.scrollLeft;
  drawTimeline();
}

function scrollTimeline() {
  const viewport = document.getElementById("timeline");
  if (!viewport || !viewport.clientWidth || !duration.value) return;
  const offset = Math.min(Math.max(viewport.scrollLeft, 0), timelineWidth());
  if (Math.abs(offset - syncedScrollLeft) <= 1.5) return;
  syncedScrollLeft = offset;
  scrubbing.value = true;
  pausePlayback();
  const time = offset / timelineWidth() * duration.value;
  currentTime.value = time;
  pendingScrubTime = time;
  seeking.value = true;
  if (!scrubFrame) scrubFrame = requestAnimationFrame(flushScrubSeek);
  drawTimeline();
  scheduleScrubEnd();
}

// Let the browser own touch/trackpad momentum. Never write scrollLeft back
// during a gesture, and let the decoder finish before requesting the latest frame.
function flushScrubSeek() {
  cancelAnimationFrame(scrubFrame);
  scrubFrame = 0;
  const video = getVideo();
  if (!video || video.seeking || pendingScrubTime === null) return;
  const target = pendingScrubTime;
  pendingScrubTime = null;
  seeking.value = Math.abs(video.currentTime - target) > 0.00001;
  if (seeking.value) video.currentTime = target;
}

function holdTimeline() {
  timelineHeld.value = true;
  pausePlayback();
  window.clearTimeout(scrubEndTimer);
}

/** @param {TouchEvent} event */
function releaseTouch(event) {
  if (event.touches.length) return;
  timelineHeld.value = false;
  scheduleScrubEnd();
}

function scheduleScrubEnd() {
  window.clearTimeout(scrubEndTimer);
  // This also covers browsers without scrollend and trackpad momentum.
  scrubEndTimer = window.setTimeout(finishScrubbing, 160);
}

function finishScrubbing() {
  if (timelineHeld.value) return;
  scrubbing.value = false;
  flushScrubSeek();
  updateTime();
}

function resetScrubbing() {
  window.clearTimeout(scrubEndTimer);
  cancelAnimationFrame(scrubFrame);
  scrubFrame = 0;
  pendingScrubTime = null;
  mousePointer = -1;
  timelineHeld.value = false;
  scrubbing.value = false;
}

/** @param {PointerEvent} event */
function startMouseDrag(event) {
  if (event.pointerType !== "mouse" || event.button !== 0) return;
  const viewport = /** @type {HTMLElement} */ (event.currentTarget);
  // Leave the native scrollbar available for direct dragging.
  if (event.clientY >= viewport.getBoundingClientRect().top + viewport.clientHeight) return;
  mousePointer = event.pointerId;
  mouseStartX = event.clientX;
  mouseStartOffset = viewport.scrollLeft;
  viewport.setPointerCapture(event.pointerId);
  holdTimeline();
}

/** @param {PointerEvent} event */
function moveMouseDrag(event) {
  if (event.pointerId !== mousePointer) return;
  const viewport = /** @type {HTMLElement} */ (event.currentTarget);
  viewport.scrollLeft = mouseStartOffset + mouseStartX - event.clientX;
}

/** @param {PointerEvent} event */
function endMouseDrag(event) {
  if (event.pointerId !== mousePointer) return;
  mousePointer = -1;
  timelineHeld.value = false;
  scheduleScrubEnd();
}

/** @param {WheelEvent} event */
function wheelTimeline(event) {
  if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
  const viewport = /** @type {HTMLElement} */ (event.currentTarget);
  let unit = 1;
  if (event.deltaMode === 1) unit = 16;
  if (event.deltaMode === 2) unit = viewport.clientWidth;
  event.preventDefault();
  viewport.scrollLeft += event.deltaY * unit;
}

/** @param {KeyboardEvent} event */
function timelineKey(event) {
  if (scrubbing.value || timelineHeld.value) { event.preventDefault(); return; }
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    event.preventDefault();
    step(event.key === "ArrowLeft" ? -1 : 1);
  }
  if (event.key === "Home" || event.key === "End") {
    event.preventDefault();
    seek(event.key === "Home" ? 0 : duration.value);
  }
  if (event.key === " ") { event.preventDefault(); void togglePlayback(); }
}

function drawTimeline() {
  const canvas = /** @type {HTMLCanvasElement | null} */ (document.getElementById("ruler"));
  const viewport = document.getElementById("timeline");
  if (!canvas || !viewport || !duration.value) return;
  const width = viewport.clientWidth;
  const ratio = window.devicePixelRatio || 1;
  const pixelWidth = Math.round(width * ratio);
  const pixelHeight = Math.round(82 * ratio);
  if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
  if (canvas.height !== pixelHeight) canvas.height = pixelHeight;
  canvas.style.width = `${width}px`;
  const context = canvas.getContext("2d");
  if (!context) return;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, 82);
  context.lineWidth = 1;
  const scale = timelineWidth() / duration.value;
  const offset = viewport.scrollLeft - width / 2;
  const tickSeconds = Math.pow(10, Math.ceil(Math.log10(65 / scale)));
  const first = Math.max(0, Math.floor(offset / scale / tickSeconds) * tickSeconds);
  const last = Math.min(duration.value, (offset + width) / scale);
  context.font = "11px ui-monospace, monospace";
  context.fillStyle = "#8d9793";
  context.strokeStyle = "#39433e";
  for (let time = first; time <= last; time += tickSeconds) {
    const x = time * scale - offset;
    context.beginPath(); context.moveTo(x, 34); context.lineTo(x, 48); context.stroke();
    context.fillText(`${Number(time.toFixed(2))}s`, x + 5, 25);
  }
  if (takeoff.value !== null && landing.value !== null) {
    context.fillStyle = "#d8eea329";
    context.fillRect(takeoff.value * scale - offset, 48, (landing.value - takeoff.value) * scale, 30);
  }
  const positions = [{ time: takeoff.value, label: "T" }, { time: landing.value, label: "L" }];
  for (const position of positions) {
    if (position.time === null) continue;
    const x = position.time * scale - offset;
    context.strokeStyle = "#d8eea3";
    context.fillStyle = context.strokeStyle;
    context.beginPath(); context.moveTo(x, 34); context.lineTo(x, 82); context.stroke();
    context.fillText(position.label, x + 5, 68);
  }
  // The playhead stays in the center, including at the start and end of a clip.
  const center = width / 2;
  context.strokeStyle = "#ffffff";
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(center, 5);
  context.lineTo(center, 82);
  context.stroke();
}

/** @param {number} time */
function formatTime(time) {
  const minutes = Math.floor(time / 60);
  const seconds = (time % 60).toFixed(3).padStart(6, "0");
  return `${minutes}:${seconds}`;
}

function syncStandalone() {
  const standalone = window.matchMedia("(display-mode: standalone)").matches || /** @type {Navigator & {standalone?: boolean}} */ (navigator).standalone;
  document.documentElement.classList.toggle("standalone", Boolean(standalone));
  syncTimeline();
}

const root = document.getElementById("app");
if (root) render(html`<${App} />`, root);
syncStandalone();
window.addEventListener("resize", syncStandalone);
window.matchMedia("(display-mode: standalone)").addEventListener("change", syncStandalone);
void setupServiceWorkerReloadWhenIdle({ isIdle: function () { return !source.value; } });
